import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Card,
  CardContent,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Assessment as AssessmentIcon,
  Share as ShareIcon,
  Close as CloseIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { chatAPI, ChatMessage } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import MessageFormatter from './MessageFormatter';
import ReviewComponent from './ReviewComponent';
import SearchResultsPanel from '../Search/SearchResultsPanel';
import tess from '../../tess.png';

interface SharedChatViewProps {
  shareToken?: string;
  isModal?: boolean;
}

const SharedChatView: React.FC<SharedChatViewProps> = ({ shareToken: propShareToken, isModal = false }) => {
  const { shareToken: urlShareToken } = useParams<{ shareToken: string }>();
  const shareToken = propShareToken || urlShareToken;
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionNotes, setSessionNotes] = useState<any[]>([]);
  const [reviewStatus, setReviewStatus] = useState<'issue' | 'pending' | 'resolved'>('issue');
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [showStatusManagement, setShowStatusManagement] = useState(false);
  const [notesMinimized, setNotesMinimized] = useState(!isModal);

  const getFieldEmoji = (field: string): string => {
    const emojiMap: { [key: string]: string } = {
      'pet': '🐕',
      'age': '📅',
      'gender': '⚧️',
      'male_reproductive': '♂️',
      'female_reproductive': '♀️',
      'symptoms': '🤒',
      'possible_symptoms': '🤔',
      'condition': '🏥',
      'history': '📋',
      'initial_symptoms': '🩺',
      'symptom_duration': '⏰',
      'diagnosis': '🔬',
      'priority': '🚨',
      'negated_pet': '🚫🐕',
      'negated_age': '🚫📅',
      'negated_gender': '🚫⚧️',
      'negated_male_reproductive': '🚫♂️',
      'negated_female_reproductive': '🚫♀️',
      'negated_symptoms': '🚫🤒',
      'negated_possible_symptoms': '🚫🤔',
      'negated_condition': '🚫🏥',
      'negated_history': '🚫📋',
      'negated_diagnosis': '🚫🔬',
    };
    return emojiMap[field] || '🔍';
  };

  const loadSharedConversation = useCallback(async () => {
    if (!shareToken) return;

    try {
      setLoading(true);
      setError(null);

      // Load session info and history
      const [sessionResponse, historyResponse] = await Promise.all([
        chatAPI.getSharedSession(shareToken),
        chatAPI.getSharedSessionHistory(shareToken)
      ]);

      console.log('📥 Session response:', sessionResponse);
      console.log('📥 History response:', historyResponse);

      setSessionData(sessionResponse);
      
      // Set initial review status if available, default to 'issue' if not set
      if (sessionResponse.review_status) {
        setReviewStatus(sessionResponse.review_status);
      } else {
        setReviewStatus('issue');
      }

      // Validate that messages exist and is an array
      if (!historyResponse || !historyResponse.messages || !Array.isArray(historyResponse.messages)) {
        console.error('❌ Invalid response structure:', historyResponse);
        setError('Invalid response from server. The shared conversation may be corrupted.');
        return;
      }

      // Format messages for display
      const formattedMessages: ChatMessage[] = historyResponse.messages.map((msg: any, index: number) => ({
        session_id: sessionResponse.id,
        session_created: false,
        user_input: msg.user_input,
        ai_response: msg.ai_response,
        extracted_info: msg.extracted_info || {},
        search_results: msg.search_results || [],
        session_context: historyResponse.session_context || {},
        processing_time_ms: msg.processing_time_ms || 0,
        message_type: msg.message_type,
        phase: msg.phase,
        completion_percentage: msg.completion_percentage,
        ready_for_diagnosis: msg.ready_for_diagnosis,
        follow_up_questions: msg.follow_up_questions || [],
      }));

      setMessages(formattedMessages);
      console.log(`✅ Loaded shared conversation with ${formattedMessages.length} messages`);

      // Extract session notes if available
      console.log('🔍 historyResponse:', JSON.stringify(historyResponse, null, 2));
      console.log('🔍 historyResponse.session_notes:', historyResponse.session_notes);
      
      if (historyResponse.session_notes) {
        setSessionNotes(historyResponse.session_notes);
        console.log(`✅ Loaded ${historyResponse.session_notes.length} session notes`);
      } else {
        console.log('❌ No session_notes found in response');
        setSessionNotes([]);
      }

    } catch (err: any) {
      console.error('Error loading shared conversation:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        url: err.config?.url,
        data: err.response?.data
      });
      
      if (err.response?.status === 404) {
        setError('Shared conversation not found. The link may have expired or been removed.');
      } else if (err.response?.status >= 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to load shared conversation. Please check if the link is valid.');
      }
    } finally {
      setLoading(false);
    }
  }, [shareToken]);

  useEffect(() => {
    if (shareToken) {
      loadSharedConversation();
    }
  }, [shareToken, loadSharedConversation]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleStatusUpdate = async () => {
    if (!sessionData?.id) return;

    try {
      setStatusUpdateLoading(true);
      await chatAPI.updateReviewStatus(sessionData.id, reviewStatus);
      setSessionData((prev: any) => ({ ...prev, review_status: reviewStatus }));
      setShowStatusManagement(false);
    } catch (err: any) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'issue': return 'error';
      case 'pending': return 'warning';
      case 'resolved': return 'success';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading shared conversation...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: isModal ? '100%' : '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* Enhanced Header */}
      <Paper sx={{ 
        p: isModal ? 2 : 3, 
        mb: 2, 
        borderRadius: 0,
        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(37, 99, 235, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        },
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}>
              <ShareIcon />
            </Avatar>
                         <Box sx={{ flex: 1 }}>
               <Typography variant="h5" sx={{ 
                 fontWeight: 600, 
                 mb: 0.5,
                 color: 'white',
                 textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
               }}>
              {sessionData?.title || 'Shared Veterinary Consultation'}
            </Typography>
               <Typography variant="body2" sx={{ 
                 color: 'rgba(255, 255, 255, 0.95)',
                 textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                 fontWeight: 500,
               }}>
              Shared by {sessionData?.owner_username} • {sessionData?.message_count} messages • 
              Created {formatTime(sessionData?.created_at)}
            </Typography>
          </Box>
                         {isAdmin && (
               <Button
                 variant="contained"
                 startIcon={<EditIcon />}
                 onClick={() => setShowStatusManagement(!showStatusManagement)}
                 sx={{
                   bgcolor: 'rgba(255, 255, 255, 0.25)',
                   color: 'white',
                   fontWeight: 600,
                   border: '1px solid rgba(255, 255, 255, 0.4)',
                   '&:hover': {
                     bgcolor: 'rgba(255, 255, 255, 0.35)',
                   },
                   backdropFilter: 'blur(10px)',
                   textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                 }}
               >
                 Manage Status
               </Button>
             )}
        </Box>
          
                     <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip 
            label="Public View" 
            size="small" 
               sx={{
                 bgcolor: 'rgba(255, 255, 255, 0.25)',
                 color: 'white',
                 border: '1px solid rgba(255, 255, 255, 0.4)',
                 fontWeight: 600,
                 backdropFilter: 'blur(4px)',
               }}
          />
          <Chip 
            label={sessionData?.status || 'Active'} 
            size="small" 
               sx={{
                 bgcolor: 'rgba(255, 255, 255, 0.25)',
                 color: 'white',
                 border: '1px solid rgba(255, 255, 255, 0.4)',
                 fontWeight: 600,
                 backdropFilter: 'blur(4px)',
               }}
             />
             {isAdmin && sessionData?.review_status && (
               <Chip 
                 label={`Review: ${sessionData.review_status.charAt(0).toUpperCase() + sessionData.review_status.slice(1)}`}
                 size="small" 
                 sx={{
                   bgcolor: getStatusColor(sessionData.review_status) === 'error' ? '#f44336' :
                           getStatusColor(sessionData.review_status) === 'warning' ? '#ff9800' : 
                           getStatusColor(sessionData.review_status) === 'success' ? '#4caf50' : '#6b7280',
                   color: 'white',
                   fontWeight: 600,
                   border: '1px solid rgba(255, 255, 255, 0.2)',
                 }}
          />
             )}
           </Box>
        </Box>
      </Paper>

      {/* Enhanced Status Management Card (only visible for admins) */}
      {isAdmin && showStatusManagement && (
        <Card sx={{ 
          mx: 2, 
          mb: 2, 
          borderRadius: 2,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
        }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ bgcolor: '#2563eb', width: 40, height: 40 }}>
                <EditIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Review Status Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Update the review status for this session
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
              gap: 3,
              mb: 3 
            }}>
              {/* Current Status */}
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f8fafc', 
                borderRadius: 2, 
                border: '1px solid #e2e8f0' 
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#64748b' }}>
                  Current Status
                </Typography>
                <Chip
                  label={sessionData?.review_status ? 
                    sessionData.review_status.charAt(0).toUpperCase() + sessionData.review_status.slice(1) : 
                    'Not Set'
                  }
                  color={getStatusColor(sessionData?.review_status || 'default') as any}
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              
                             {/* Session Info */}
               <Box sx={{ 
                 p: 2, 
                 bgcolor: '#f8fafc', 
                 borderRadius: 2, 
                 border: '1px solid #e2e8f0' 
               }}>
                 <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#64748b' }}>
                   Session Info
                 </Typography>
                 <Typography variant="body2">
                   {sessionNotes.length} note{sessionNotes.length !== 1 ? 's' : ''} • {sessionData?.message_count} messages
                 </Typography>
                 <Typography variant="body2" sx={{ mt: 0.5, color: '#64748b' }}>
                   Admin Review Access
                 </Typography>
               </Box>
            </Box>
            
            {/* Status Update Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Update Status</InputLabel>
                <Select
                  value={reviewStatus}
                  label="Update Status"
                  onChange={(e) => setReviewStatus(e.target.value as 'issue' | 'pending' | 'resolved')}
                  disabled={statusUpdateLoading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                >
                  <MenuItem value="issue">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f44336' }} />
                      Issue
                    </Box>
                  </MenuItem>
                  <MenuItem value="pending">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff9800' }} />
                      Pending
                    </Box>
                  </MenuItem>
                  <MenuItem value="resolved">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4caf50' }} />
                      Resolved
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              
              <Button
                variant="contained"
                onClick={handleStatusUpdate}
                disabled={statusUpdateLoading || reviewStatus === sessionData?.review_status}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: '#2563eb',
                  '&:hover': {
                    bgcolor: '#1d4ed8',
                  },
                  '&:disabled': {
                    bgcolor: '#e2e8f0',
                    color: '#94a3b8',
                  },
                }}
              >
                {statusUpdateLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    Updating...
                  </Box>
                ) : (
                  'Update Status'
                )}
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => setShowStatusManagement(false)}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: '#e2e8f0',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Content Area with Dynamic Split */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: isModal ? 'row' : 'column', 
        overflow: 'hidden' 
      }}>
        {/* Messages Section - Dynamic height based on notes state */}
        <Box sx={{ 
          height: isModal ? '100%' : (notesMinimized ? '100%' : '60%'),
          width: isModal ? '60%' : '100%',
          overflow: 'auto', 
          p: 2,
          borderBottom: isModal ? 'none' : '1px solid #e2e8f0',
          borderRight: isModal ? '1px solid #e2e8f0' : 'none',
          transition: 'height 0.3s ease-in-out',
        }}>
          {messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                This conversation doesn't have any messages yet.
              </Typography>
            </Box>
          ) : (
            messages.map((msg, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                {/* User Message */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Paper sx={{ 
                    p: 2, 
                    maxWidth: '70%', 
                    background: 'linear-gradient(135deg,rgb(134, 161, 238) 0%,rgb(78, 111, 217) 100%)', 
                    color: 'white',
                    borderRadius: '18px 18px 4px 18px',
                    boxShadow: '0 4px 12px rgba(29, 78, 216, 0.25)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}>
                    <Typography variant="body1" sx={{ 
                      fontWeight: 500,
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    }}>
                      {msg.user_input}
                    </Typography>
                  </Paper>
                </Box>

                {/* AI Response */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 1 }}>
                  <Avatar
                    src={tess}
                    alt="Tess AI"
                    sx={{ width: 32, height: 32, mt: 0.5 }}
                  />
                  <Paper sx={{ 
                    p: 2, 
                    maxWidth: '70%', 
                    bgcolor: 'white',
                    borderRadius: '18px 18px 18px 4px'
                  }}>
                    {/* Use formatted display for diagnosis messages and other structured content */}
                    <MessageFormatter 
                      content={msg.ai_response}
                      searchResults={msg.search_results}
                      isPublicView={true}
                    />

                    {/* Extracted Information Display */}
                    {msg.extracted_info && Object.keys(msg.extracted_info).length > 0 && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          borderRadius: 2,
                          fontSize: '0.875rem',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#22537f', fontWeight: 600 }}>
                          📊 Extracted Information:
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Object.entries(msg.extracted_info).map(([key, value]) => {
                            if (!value || (Array.isArray(value) && value.length === 0)) return null;
                            
                            // Hide internal system fields
                            const hiddenFields = ['base_questions_complete', 'structured_symptoms_asked', 'initial_symptoms'];
                            if (hiddenFields.includes(key)) return null;
                            
                            const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
                            
                            return (
                              <Box 
                                key={key}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  px: 1,
                                  py: 0.5,
                                  bgcolor: '#ffffff',
                                  border: '1px solid #b8ccde',
                                  borderRadius: 1,
                                  fontSize: '0.75rem'
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#22537f' }}>
                                  {getFieldEmoji(key)} {key.replace('_', ' ').replace('negated ', '❌ ')}:
                                </Typography>
                                <Typography variant="caption">
                                  {displayValue}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                  </Paper>
                </Box>

                {/* Search Results for this message */}
                {msg.search_results && msg.search_results.length > 0 && (
                  <Box sx={{ ml: 5, mt: 2 }}>
                    <SearchResultsPanel results={msg.search_results} maxHeight="300px" />
                  </Box>
                )}
              </Box>
            ))
          )}
        </Box>

        {/* Enhanced Session Notes Section - Collapsible */}
        <Box sx={{ 
          height: isModal ? '100%' : (notesMinimized ? 'auto' : '40%'),
          width: isModal ? '40%' : '100%',
          bgcolor: '#ffffff', 
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderTop: isModal ? 'none' : '1px solid #e2e8f0',
          transition: 'height 0.3s ease-in-out',
          minHeight: isModal ? '100%' : (notesMinimized ? '60px' : '40%'),
        }}>
          <Box sx={{ 
            p: 2, 
            borderBottom: notesMinimized ? 'none' : '1px solid #e2e8f0',
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            color: 'white',
            cursor: 'pointer',
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
            },
            transition: 'background 0.2s ease-in-out',
          }}
          onClick={() => !isModal && setNotesMinimized(!notesMinimized)}
          style={{ cursor: isModal ? 'default' : 'pointer' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', width: 32, height: 32 }}>
                📝
              </Avatar>
                             <Box sx={{ flex: 1 }}>
                 <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                   Session Notes & Reviews
                 </Typography>
                 {isAdmin && !notesMinimized && sessionData?.review_status && (
                   <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                     Status: {sessionData.review_status.charAt(0).toUpperCase() + sessionData.review_status.slice(1)}
            </Typography>
                 )}
               </Box>
              {sessionNotes.length >= 0 && (
                <Chip
                  label={`${sessionNotes.length} Notes`}
                  size="small"
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 600,
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                  }}
                />
              )}
              {!isModal && (
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: 'white', 
                    ml: 1,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  {notesMinimized ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
              )}
            </Box>
          </Box>
          {(!notesMinimized || isModal) && (
          <Box sx={{ 
            flex: 1, 
            overflow: 'auto', 
              p: 3,
            '&::-webkit-scrollbar': {
                width: '8px',
            },
            '&::-webkit-scrollbar-track': {
                background: '#f1f5f9',
                borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
                background: '#cbd5e1',
                borderRadius: '4px',
                '&:hover': {
                  background: '#94a3b8',
                },
            },
          }}>
              {sessionNotes.length === 0 ? (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  bgcolor: '#f8fafc',
                  borderRadius: 2,
                  border: '2px dashed #e2e8f0',
                }}>
                  <Typography variant="h6" sx={{ color: '#64748b', mb: 1 }}>
                    No notes available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    This session doesn't have any notes or reviews yet.
                  </Typography>
                </Box>
              ) : (
            <ReviewComponent 
              sessionId={sessionData?.id || ''}
              isPublicView={true}
              initialReviews={sessionNotes}
              showAddButton={false}
            />
              )}
          </Box>
          )}
        </Box>
      </Box>


    </Box>
  );
};

export default SharedChatView; 